import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Download,
  ImageOff,
  Save,
  Search,
  SlidersHorizontal,
  TriangleAlert,
  ChevronRight
} from "lucide-react";
import api from "../api/api";
import { getKategoriFromKode } from "../utils/productCode";
import { useDialog } from "../components/DialogProvider";
import { useAppSettings } from "../settings/AppSettingsProvider";
import { includesSearch } from "../utils/search";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function ProductIcon({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";
  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img src={src} alt={alt} className="h-10 w-10 shrink-0 rounded-md border border-zinc-200 object-cover" loading="lazy" onError={() => setBroken(true)} />
  );
}

function SelisihBadge({ value }) {
  const n = Number(value) || 0;
  if (n === 0) return <span className="font-semibold text-zinc-500 tabular-nums">0</span>;
  if (n > 0) return <span className="font-semibold text-emerald-600 tabular-nums">+{n}</span>;
  return <span className="font-semibold text-rose-600 tabular-nums">{n}</span>;
}

function StatusBadge({ selisih, sisaSistem, lowStockThreshold }) {
  const sel  = Number(selisih) || 0;
  const sisa = Number(sisaSistem) || 0;
  
  if (sel !== 0) {
    return <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">Tidak Cocok</span>;
  }
  if (sisa === 0) {
    return <span className="inline-flex items-center rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">Habis</span>;
  }
  if (sisa < lowStockThreshold) {
    return <span className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-[11px] font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">Menipis</span>;
  }
  return <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">Aman</span>;
}


function SourceStatCard({ label, value, hint, tone = "neutral" }) {
  const isDefect = tone === "defect";
  const isSpare = tone === "spare";
  
  const bgClass = isDefect ? "bg-rose-50 border-rose-200" : isSpare ? "bg-zinc-50 border-zinc-200" : "bg-white border-zinc-200";
  const labelClass = isDefect ? "text-rose-600" : "text-zinc-500";
  const valueClass = isDefect ? "text-rose-700" : "text-zinc-900";

  return (
    <div className={`min-w-0 rounded-md border px-4 py-3 ${bgClass}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider ${labelClass}`}>{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${valueClass}`}>{Math.max(0, Math.trunc(Number(value) || 0))}</div>
      {hint && <div className={`mt-1 truncate text-[11px] ${labelClass}`}>{hint}</div>}
    </div>
  );
}


const sizeWeights = {
  "XXS": 1,
  "XS": 2,
  "S": 3,
  "M": 4,
  "L": 5,
  "XL": 6,
  "XXL": 7,
  "2XL": 7,
  "XXXL": 8,
  "3XL": 8,
};

function parseSizeFromName(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0) return { baseName: name, sizeWeight: 0 };
  const lastWord = parts[parts.length - 1].toUpperCase();
  if (sizeWeights[lastWord] !== undefined) {
    const baseName = parts.slice(0, -1).join(" ");
    return { baseName, sizeWeight: sizeWeights[lastWord] };
  }
  return { baseName: name, sizeWeight: 0 };
}

export default function StockOpname() {
  const dialog = useDialog();
  const { settings } = useAppSettings();
  const lowStockThresholdRaw = settings?.lowStockThreshold ?? 5;
  const lowStockThreshold = (() => {
    const n = Math.trunc(Number(lowStockThresholdRaw));
    return Number.isFinite(n) && n > 0 ? n : 5;
  })();

  const [q, setQ]               = useState("");
  const [kategori, setKategori] = useState("ALL");
  const [sortBy, setSortBy]     = useState(null);
  const [sortDir, setSortDir]   = useState("asc");
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [exportFrom, setExportFrom] = useState(todayISO());
  const [exportTo, setExportTo]     = useState(todayISO());
  const [expandedKode, setExpandedKode] = useState(null);
  const [defectModal, setDefectModal] = useState({
    open: false, kode_barang: "", nama_barang: "", source_from: "Spare",
    qty: 1, reason: "", source_stocks: { Shopee: 0, Tokopedia: 0, Spare: 0 },
  });
  const [allocationModal, setAllocationModal] = useState({
    open: false, kode_barang: "", nama_barang: "", sisa_stok: 0, shopee: 0, tokopedia: 0,
  });
  const [movingDefect, setMovingDefect] = useState(false);

  const fetchRekap = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const res  = await api.get("/stock-opname/rekap");
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(
        data.map((r) => ({
          kode_barang:    r.kode_barang,
          nama_barang:    r.nama_barang,
          image_url:      r.image_url,
          stok_masuk:     Number(r.stok_masuk)    || 0,
          stok_keluar:    Number(r.stok_keluar)   || 0,
          sisa_stok:      Number(r.sisa_stok)     || 0,
          alok_shopee:    Number(r.alok_shopee)   || 0,
          alok_tokopedia: Number(r.alok_tokopedia)|| 0,
          alok_defect:    Number(r.alok_defect)   || 0,
          stok_fisik:     Number(r.sisa_stok) || 0,
          selisih:        0,
          keterangan:     r.keterangan || "",
          last_opname:    r.last_opname_tanggal || null,
          status: Number(r.sisa_stok) <= 0 ? 0 : Number(r.sisa_stok) <= lowStockThreshold ? 1 : 2,
        }))
      );
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal mengambil rekap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRekap(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kategoriOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { const k = getKategoriFromKode(r.kode_barang); if (k) set.add(k); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const query = q.trim();
    let filtered = rows.filter((r) => {
      const cat = getKategoriFromKode(r.kode_barang);
      const matchKategori = kategori === "ALL" ? true : cat === kategori;
      const matchQuery = !query || includesSearch(`${r.kode_barang || ""} ${r.nama_barang || ""}`, query);
      return matchKategori && matchQuery;
    });
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === "nama_barang") {
          const aParsed = parseSizeFromName(a.nama_barang);
          const bParsed = parseSizeFromName(b.nama_barang);
          
          const nameCmp = aParsed.baseName.localeCompare(bParsed.baseName);
          if (nameCmp !== 0) {
             return sortDir === "asc" ? nameCmp : -nameCmp;
          }
          return sortDir === "asc" ? aParsed.sizeWeight - bParsed.sizeWeight : bParsed.sizeWeight - aParsed.sizeWeight;
        }

        const aVal = sortBy === "stok_sistem" ? Number(a.sisa_stok) : sortBy === "selisih" ? Number(a.selisih) : Number(a.status);
        const bVal = sortBy === "stok_sistem" ? Number(b.sisa_stok) : sortBy === "selisih" ? Number(b.selisih) : Number(b.status);
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      });
    } else {
      // Default sorting: alphabetically by name, then by size (ascending)
      filtered = [...filtered].sort((a, b) => {
        const aParsed = parseSizeFromName(a.nama_barang);
        const bParsed = parseSizeFromName(b.nama_barang);
        const nameCmp = aParsed.baseName.localeCompare(bParsed.baseName);
        if (nameCmp !== 0) return nameCmp;
        return aParsed.sizeWeight - bParsed.sizeWeight;
      });
    }
    return filtered;
  }, [rows, q, kategori, sortBy, sortDir]);

  const handleFisikChange = (kode, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.kode_barang !== kode) return r;
        if (value === "") return { ...r, stok_fisik: "", selisih: 0 - (Number(r.sisa_stok) || 0) };
        const stokFisik = Math.trunc(Number(value));
        if (Number.isFinite(stokFisik) && stokFisik >= 0) {
          return { ...r, stok_fisik: stokFisik, selisih: stokFisik - (Number(r.sisa_stok) || 0) };
        }
        return { ...r, stok_fisik: "", selisih: 0 - (Number(r.sisa_stok) || 0) };
      })
    );
  };

  const handleKetChange  = (kode, value) => setRows((prev) => prev.map((r) => r.kode_barang === kode ? { ...r, keterangan: value } : r));
  const toggleExpand     = (kode) => setExpandedKode((prev) => prev === kode ? null : kode);
  const handleSort       = (column) => {
    if (sortBy === column) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortBy(null); setSortDir("asc"); }
    } else {
      setSortBy(column); setSortDir("asc");
    }
  };

  const SortableHeader = ({ label, column, align = "left" }) => {
    const isActive = sortBy === column;
    const arrow    = isActive ? (sortDir === "asc" ? "▲" : "▼") : "";
    const arrowColor = isActive ? "text-zinc-900" : "text-zinc-300";
    return (
      <th onClick={() => handleSort(column)} className={`px-4 py-3 font-medium cursor-pointer select-none hover:text-zinc-900 transition-colors ${align === "right" ? "text-right" : "text-left"}`}>
        <div className={`flex items-center gap-1.5 ${align === "right" ? "justify-end" : ""}`}>
          <span>{label}</span>
          {arrow && <span className={`${arrowColor} text-[10px]`}>{arrow}</span>}
        </div>
      </th>
    );
  };

  const handleSave = async () => {
    const invalidAlloc = rows.find((r) => {
      if (getKategoriFromKode(r.kode_barang) !== "PRODUK UTAMA") return false;
      const sisa = Math.max(0, Math.trunc(Number(r.sisa_stok) || 0));
      return (Math.trunc(Number(r.alok_shopee) || 0) + Math.trunc(Number(r.alok_tokopedia) || 0)) > sisa;
    });
    if (invalidAlloc) {
      const message = `Alokasi Shopee + Tokopedia melebihi sisa stok sistem untuk ${invalidAlloc.kode_barang}.`;
      setError(message);
      dialog.toast({ type: "error", title: "Alokasi tidak valid", message });
      return;
    }

    const ok = await dialog.confirm(
      `Yakin simpan Stock Opname sekarang? Data opname fisik hari ini akan disimpan sebagai audit trail.`,
      { title: "Simpan Stock Opname", confirmText: "Simpan", cancelText: "Batal" }
    );
    if (!ok) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const payload = {
        rows: rows.map((r) => ({
          kode_barang:    r.kode_barang,
          stok_fisik:     Number(r.stok_fisik) || 0,
          alok_shopee:    getKategoriFromKode(r.kode_barang) === "PRODUK UTAMA" ? Number(r.alok_shopee) || 0 : 0,
          alok_tokopedia: getKategoriFromKode(r.kode_barang) === "PRODUK UTAMA" ? Number(r.alok_tokopedia) || 0 : 0,
          keterangan:     r.keterangan || "",
        })),
      };

      const res = await dialog.toastPromise(
        api.post("/stock-opname", payload),
        {
          loading: "Menyimpan stock opname…",
          success: (r) => r?.data?.message || "Stock opname tersimpan.",
          error:   (e2) => e2?.response?.data?.message || e2.message || "Gagal menyimpan.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );
      setSuccess(res?.data?.message || "Tersimpan");
      await fetchRekap();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const getSourceStocks = (row) => {
    const sisa      = Math.max(0, Math.trunc(Number(row?.sisa_stok) || 0));
    const shopee    = Math.max(0, Math.trunc(Number(row?.alok_shopee) || 0));
    const tokopedia = Math.max(0, Math.trunc(Number(row?.alok_tokopedia) || 0));
    const canAlloc  = getKategoriFromKode(row?.kode_barang) === "PRODUK UTAMA";
    const spare     = canAlloc ? Math.max(0, sisa - shopee - tokopedia) : sisa;
    return { Shopee: canAlloc ? shopee : 0, Tokopedia: canAlloc ? tokopedia : 0, Spare: spare };
  };

  const openDefectMove = (row, sourceFrom = "Spare") => {
    const sourceStocks = getSourceStocks(row);
    const selected = sourceStocks[sourceFrom] > 0 ? sourceFrom
      : ["Spare", "Shopee", "Tokopedia"].find((s) => sourceStocks[s] > 0) || "Spare";
    setDefectModal({ open: true, kode_barang: row.kode_barang, nama_barang: row.nama_barang || "",
      source_from: selected, qty: 1, reason: "", source_stocks: sourceStocks });
  };

  const closeDefectMove     = () => { if (movingDefect) return; setDefectModal((p) => ({ ...p, open: false })); };
  const openAllocationEdit  = (row) => setAllocationModal({ open: true, kode_barang: row.kode_barang, nama_barang: row.nama_barang || "",
    sisa_stok: Math.max(0, Math.trunc(Number(row.sisa_stok) || 0)),
    shopee: Math.max(0, Math.trunc(Number(row.alok_shopee) || 0)),
    tokopedia: Math.max(0, Math.trunc(Number(row.alok_tokopedia) || 0)) });
  const closeAllocationEdit = () => setAllocationModal((p) => ({ ...p, open: false }));

  const submitAllocationEdit = (e) => {
    e.preventDefault();
    const shopee    = Math.trunc(Number(allocationModal.shopee));
    const tokopedia = Math.trunc(Number(allocationModal.tokopedia));
    const sisa      = Math.max(0, Math.trunc(Number(allocationModal.sisa_stok) || 0));
    if (!Number.isFinite(shopee) || !Number.isFinite(tokopedia) || shopee < 0 || tokopedia < 0) {
      dialog.toast({ type: "error", title: "Alokasi tidak valid", message: "Tidak boleh negatif." }); return;
    }
    if (shopee + tokopedia > sisa) {
      dialog.toast({ type: "error", title: "Alokasi tidak valid", message: `Total alokasi maksimal ${sisa}.` }); return;
    }
    setRows((prev) => prev.map((r) => r.kode_barang === allocationModal.kode_barang ? { ...r, alok_shopee: shopee, alok_tokopedia: tokopedia } : r));
    setAllocationModal((p) => ({ ...p, open: false }));
  };

  const submitDefectMove = async (e) => {
    e.preventDefault();
    const qty       = Math.trunc(Number(defectModal.qty));
    const available = Math.max(0, Math.trunc(Number(defectModal.source_stocks?.[defectModal.source_from]) || 0));
    if (!defectModal.kode_barang || !Number.isFinite(qty) || qty <= 0) {
      dialog.toast({ type: "error", title: "Defect tidak valid", message: "Qty harus lebih dari 0." }); return;
    }
    if (qty > available) {
      dialog.toast({ type: "error", title: "Stok tidak cukup", message: `Stok tersedia: ${available}.` }); return;
    }
    try {
      setMovingDefect(true);
      await dialog.toastPromise(
        api.post("/defect/move", { kode_barang: defectModal.kode_barang, source_from: defectModal.source_from, qty, reason: defectModal.reason }),
        { loading: "Memindahkan stok defect...", success: (r) => r?.data?.message || "Berhasil.", error: (err) => err?.response?.data?.message || "Gagal." },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );
      setDefectModal((p) => ({ ...p, open: false }));
      await fetchRekap();
    } finally {
      setMovingDefect(false);
    }
  };

  const handleOpenExport = async () => {
    const ok = await dialog.custom(
      <div className="space-y-4">
        <div>
          <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Dari Tanggal</label>
          <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Sampai Tanggal</label>
          <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" />
        </div>
      </div>,
      { title: "Export CSV Stock Opname", confirmText: "Download", cancelText: "Batal" }
    );
    if (!ok) return;
    try {
      setExporting(true);
      const res = await api.get(`/stock-opname/export?from=${exportFrom}&to=${exportTo}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `stock-opname-${exportFrom}_to_${exportTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setExporting(false);
    }
  };

  const totalSelisih = useMemo(() => visibleRows.reduce((s, r) => s + (Number(r.selisih) || 0), 0), [visibleRows]);

  const stockStatusSummary = useMemo(() => {
    let habis = 0, menipis = 0, aman = 0;
    visibleRows.forEach((r) => {
      const sisa = Number(r.sisa_stok) || 0;
      if (sisa <= 0) habis++;
      else if (sisa <= lowStockThreshold) menipis++;
      else aman++;
    });
    return { habis, menipis, aman, total: visibleRows.length };
  }, [visibleRows, lowStockThreshold]);

  const renderStockDetailPanel = (r) => {
    const sisa      = Math.max(0, Math.trunc(Number(r.sisa_stok) || 0));
    const shopee    = Math.max(0, Math.trunc(Number(r.alok_shopee) || 0));
    const tokopedia = Math.max(0, Math.trunc(Number(r.alok_tokopedia) || 0));
    const defect    = Math.max(0, Math.trunc(Number(r.alok_defect) || 0));
    const canAlloc  = getKategoriFromKode(r.kode_barang) === "PRODUK UTAMA";
    const spare     = canAlloc ? Math.max(0, sisa - shopee - tokopedia) : sisa;
    const totalMoveable = (canAlloc ? shopee + tokopedia : 0) + spare;

    return (
      <div className="rounded-md border border-zinc-200 bg-white p-4 animate-fade-in shadow-sm m-4 mt-0">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <div className="text-[13px] font-semibold text-zinc-900">Alokasi Stok & Detail Channel</div>
            <div className="mt-0.5 text-[11px] text-zinc-500">
              {r.last_opname ? `Terakhir opname: ${r.last_opname}` : "Belum pernah opname"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canAlloc && (
              <button type="button" onClick={(e) => { e.stopPropagation(); openAllocationEdit(r); }}
                className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">
                Atur Alokasi Channel
              </button>
            )}
            <button type="button"
              onClick={(e) => { e.stopPropagation(); openDefectMove(r, spare > 0 ? "Spare" : shopee > 0 ? "Shopee" : "Tokopedia"); }}
              disabled={totalMoveable <= 0}
              className={`inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-[11px] font-medium shadow-sm transition-colors ${
                totalMoveable <= 0 
                  ? "border-zinc-200 bg-zinc-100 text-zinc-400 cursor-not-allowed" 
                  : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
              }`}>
              Pindah ke Defect
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-4">
          <SourceStatCard label="Shopee" value={canAlloc ? shopee : 0} hint={canAlloc ? "Alokasi channel" : "N/A"} />
          <SourceStatCard label="Tokopedia" value={canAlloc ? tokopedia : 0} hint={canAlloc ? "Alokasi channel" : "N/A"} />
          <SourceStatCard label="Gudang (Spare)" value={spare} hint="Stok bebas jual" tone="spare" />
          <SourceStatCard label="Defect" value={defect} hint="Barang cacat" tone="defect" />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md bg-zinc-50 border border-zinc-100 px-3 py-2 flex justify-between items-center">
            <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Histori Masuk</span>
            <span className="font-semibold text-zinc-900">{Number(r.stok_masuk) || 0}</span>
          </div>
          <div className="rounded-md bg-zinc-50 border border-zinc-100 px-3 py-2 flex justify-between items-center">
            <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Histori Keluar</span>
            <span className="font-semibold text-zinc-900">{Number(r.stok_keluar) || 0}</span>
          </div>
        </div>
      </div>
    );
  };

  const modalStocks    = defectModal.source_stocks || { Shopee: 0, Tokopedia: 0, Spare: 0 };
  const modalAvailable = Math.max(0, Math.trunc(Number(modalStocks[defectModal.source_from]) || 0));
  const modalQty       = Math.trunc(Number(defectModal.qty));
  const modalQtyInvalid = !Number.isFinite(modalQty) || modalQty <= 0 || modalQty > modalAvailable;
  const allocationShopee    = Math.max(0, Math.trunc(Number(allocationModal.shopee)    || 0));
  const allocationTokopedia = Math.max(0, Math.trunc(Number(allocationModal.tokopedia) || 0));
  const allocationSisa      = Math.max(0, Math.trunc(Number(allocationModal.sisa_stok) || 0));
  const allocationTotal     = allocationShopee + allocationTokopedia;
  const allocationSpare     = Math.max(0, allocationSisa - allocationTotal);
  const allocationInvalid   = allocationTotal > allocationSisa;

  const defectMoveModal = defectModal.open && typeof document !== "undefined"
    ? createPortal(
        <div className="fixed inset-0 z-[1000] flex h-screen w-screen items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
          <form onSubmit={submitDefectMove} className="w-full max-w-[420px] rounded-lg border border-zinc-200 bg-white shadow-xl animate-fade-in flex flex-col">
            <div className="border-b border-zinc-100 px-5 py-4">
              <h3 className="text-sm font-semibold text-zinc-900">Pindahkan ke Defect</h3>
              <p className="mt-1 text-[11px] text-zinc-500">
                Pindahkan stok produk <span className="font-semibold text-zinc-700">{defectModal.kode_barang}</span> ke penyimpanan cacat.
              </p>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Source Asal</label>
                <select value={defectModal.source_from}
                  onChange={(e) => { const next = e.target.value; const avail = Math.max(0, Math.trunc(Number(modalStocks[next]) || 0));
                    setDefectModal((p) => ({ ...p, source_from: next, qty: Math.min(Math.max(1, Math.trunc(Number(p.qty) || 1)), Math.max(1, avail)) })); }}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" disabled={movingDefect}>
                  {["Spare", "Shopee", "Tokopedia"].map((s) => {
                    const av = Math.max(0, Math.trunc(Number(modalStocks[s]) || 0));
                    return <option key={s} value={s} disabled={av <= 0}>{s} - tersedia {av}</option>;
                  })}
                </select>
                <div className="mt-1.5 text-[11px] text-zinc-500">
                  Tersedia untuk dipindah: <span className="font-semibold text-zinc-900">{modalAvailable}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Qty (Jumlah Defect)</label>
                <input type="number" min="1" max={Math.max(1, modalAvailable)} value={defectModal.qty}
                  onChange={(e) => setDefectModal((p) => ({ ...p, qty: e.target.value }))}
                  className={`w-full rounded-md border px-3 py-2 text-[13px] text-zinc-900 focus:outline-none focus:ring-1 shadow-sm ${modalQtyInvalid && defectModal.qty !== "" ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500" : "border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900"}`}
                  disabled={movingDefect || modalAvailable <= 0} />
              </div>
              
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Alasan / Keterangan</label>
                <textarea value={defectModal.reason} onChange={(e) => setDefectModal((p) => ({ ...p, reason: e.target.value }))}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm min-h-[80px]" placeholder="Contoh: jahitan lepas, noda kotor..." disabled={movingDefect} />
              </div>
            </div>
            
            <div className="border-t border-zinc-100 bg-zinc-50/50 px-5 py-3 flex justify-end gap-2 rounded-b-lg">
              <button type="button" onClick={closeDefectMove} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors" disabled={movingDefect}>Batal</button>
              <button type="submit" className="rounded-md bg-zinc-900 px-3 py-1.5 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50" disabled={movingDefect || modalAvailable <= 0 || modalQtyInvalid}>
                Pindahkan ke Defect
              </button>
            </div>
          </form>
        </div>, document.body) : null;

  const allocationEditModal = allocationModal.open && typeof document !== "undefined"
    ? createPortal(
        <div className="fixed inset-0 z-[1000] flex h-screen w-screen items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
          <form onSubmit={submitAllocationEdit} className="w-full max-w-[420px] rounded-lg border border-zinc-200 bg-white shadow-xl animate-fade-in flex flex-col">
            <div className="border-b border-zinc-100 px-5 py-4">
              <h3 className="text-sm font-semibold text-zinc-900">Alokasi Channel E-Commerce</h3>
              <p className="mt-1 text-[11px] text-zinc-500">
                Atur pembagian porsi stok untuk <span className="font-semibold text-zinc-700">{allocationModal.kode_barang}</span>
              </p>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 flex justify-between items-center">
                <span className="text-[12px] font-medium text-zinc-600">Total Stok Bebas Jual (Sistem)</span>
                <span className="text-[14px] font-bold text-zinc-900">{allocationSisa}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Porsi Shopee</label>
                  <input type="number" min="0" value={allocationModal.shopee} onChange={(e) => setAllocationModal((p) => ({ ...p, shopee: e.target.value }))} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 text-center font-medium focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Porsi Tokopedia</label>
                  <input type="number" min="0" value={allocationModal.tokopedia} onChange={(e) => setAllocationModal((p) => ({ ...p, tokopedia: e.target.value }))} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 text-center font-medium focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" />
                </div>
              </div>
              
              <div className={`rounded-md border p-3 flex justify-between items-center ${allocationInvalid ? "border-rose-200 bg-rose-50" : "border-zinc-100 bg-zinc-50"}`}>
                <div className="flex flex-col">
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${allocationInvalid ? "text-rose-600" : "text-zinc-500"}`}>Gudang (Spare)</span>
                  <span className={`text-[12px] ${allocationInvalid ? "text-rose-700" : "text-zinc-600"}`}>Otomatis menyesuaikan</span>
                </div>
                <div className={`text-lg font-bold ${allocationInvalid ? "text-rose-700" : "text-zinc-900"}`}>
                  {allocationInvalid ? "INVALID" : allocationSpare}
                </div>
              </div>
              
              {allocationInvalid && (
                <div className="text-[11px] text-rose-600 flex items-center gap-1">
                  <TriangleAlert className="h-3 w-3" />
                  Total alokasi channel ({allocationTotal}) melebihi stok yang ada ({allocationSisa}).
                </div>
              )}
            </div>
            
            <div className="border-t border-zinc-100 bg-zinc-50/50 px-5 py-3 flex justify-end gap-2 rounded-b-lg">
              <button type="button" onClick={closeAllocationEdit} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">Batal</button>
              <button type="submit" className="rounded-md bg-zinc-900 px-3 py-1.5 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50" disabled={allocationInvalid}>
                Terapkan Alokasi
              </button>
            </div>
          </form>
        </div>, document.body) : null;

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Stock Opname</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Penyesuaian stok sistem dengan jumlah fisik. Simpan untuk audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleOpenExport} disabled={exporting} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">
            <Download className="h-4 w-4" />
            {exporting ? "Mengekspor..." : "Export Laporan"}
          </button>
          <button onClick={handleSave} disabled={saving || loading || rows.length === 0} className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50">
            {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" /> : <Save className="h-4 w-4" />}
            {saving ? "Menyimpan Opname..." : "Simpan Hasil Opname"}
          </button>
        </div>
      </header>

      {error && <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[13px] text-red-600">{error}</div>}
      {success && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-700">{success}</div>}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="text-[12px] font-medium text-zinc-500 mb-2">Total Produk Habis</div>
          <div className="text-2xl font-bold text-rose-600 tabular-nums">{stockStatusSummary.habis}</div>
          <div className="mt-1 text-[10px] text-zinc-400">Stok sistem = 0</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="text-[12px] font-medium text-zinc-500 mb-2">Stok Menipis</div>
          <div className="text-2xl font-bold text-amber-600 tabular-nums">{stockStatusSummary.menipis}</div>
          <div className="mt-1 text-[10px] text-zinc-400">Batas ambang: {lowStockThreshold}</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="text-[12px] font-medium text-zinc-500 mb-2">Stok Aman</div>
          <div className="text-2xl font-bold text-emerald-600 tabular-nums">{stockStatusSummary.aman}</div>
          <div className="mt-1 text-[10px] text-zinc-400">Lebih dari {lowStockThreshold}</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm bg-gradient-to-br from-white to-zinc-50">
          <div className="text-[12px] font-medium text-zinc-500 mb-2">Total Selisih Fisik</div>
          <div className={`text-2xl font-bold tabular-nums ${totalSelisih === 0 ? 'text-zinc-900' : totalSelisih > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {totalSelisih > 0 ? `+${totalSelisih}` : totalSelisih}
          </div>
          <div className="mt-1 text-[10px] text-zinc-400">Pengecekan hari ini</div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 p-4">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-zinc-900 sm:w-48 shrink-0">
          <SlidersHorizontal className="h-4 w-4 text-zinc-400" /> 
          Filter Pencarian
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select value={kategori} onChange={(e) => setKategori(e.target.value)} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white">
            <option value="ALL">Semua Kategori Produk</option>
            {kategoriOptions.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full rounded-md border border-zinc-300 pl-9 pr-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" placeholder="Cari nama atau kode produk..." />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-left text-[13px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
              <tr>
                <th className="w-10 px-3 py-3"></th>
                <SortableHeader label="Informasi Produk" column="nama_barang" />
                <SortableHeader label="Sistem" column="stok_sistem" align="right" />
                <th className="px-4 py-3 text-center font-medium">Hitung Fisik</th>
                <SortableHeader label="Selisih" column="selisih" align="right" />
                <SortableHeader label="Status" column="status" />
                <th className="px-4 py-3 font-medium">Catatan / Alasan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">Memuat data produk...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">Tidak ada data produk.</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">Tidak ada produk yang cocok dengan pencarian.</td></tr>
              ) : visibleRows.map((r) => {
                const sisa     = Number(r.sisa_stok) || 0;
                const sel      = Number(r.selisih) || 0;
                const expanded = expandedKode === r.kode_barang;
                
                // Color coding for rows with selisih
                const rowBg = sel !== 0 ? "bg-amber-50/30 hover:bg-amber-50/50" : "bg-white hover:bg-zinc-50/50";
                
                return (
                  <Fragment key={r.kode_barang}>
                    <tr className={`${rowBg} cursor-pointer transition-colors`} onClick={() => toggleExpand(r.kode_barang)}>
                      <td className="px-3 py-3 text-center align-middle">
                        <button type="button" className={`inline-flex h-6 w-6 items-center justify-center rounded border ${expanded ? 'border-zinc-300 bg-zinc-100 text-zinc-700' : 'border-transparent text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'} transition-all`} onClick={(e) => { e.stopPropagation(); toggleExpand(r.kode_barang); }}>
                          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <ProductIcon imageUrl={r.image_url} alt={r.nama_barang || r.kode_barang} />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-zinc-900">{r.nama_barang}</div>
                            <div className="mt-0.5 font-mono text-[11px] text-zinc-500">{r.kode_barang}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-zinc-900 text-[14px]">
                        {sisa}
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center">
                          <input 
                            type="number" 
                            value={r.stok_fisik} 
                            onChange={(e) => handleFisikChange(r.kode_barang, e.target.value)}
                            className={`w-24 rounded-md border px-2 py-1.5 text-center font-bold tabular-nums text-[14px] focus:outline-none focus:ring-1 shadow-sm transition-colors ${
                              sel !== 0 
                                ? "border-amber-400 bg-amber-50 text-amber-900 focus:border-amber-500 focus:ring-amber-500" 
                                : "border-zinc-300 bg-white text-zinc-900 focus:border-zinc-900 focus:ring-zinc-900"
                            }`} 
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-[14px]">
                        <SelisihBadge value={sel} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge selisih={sel} sisaSistem={sisa} lowStockThreshold={lowStockThreshold} />
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input 
                          value={r.keterangan} 
                          onChange={(e) => handleKetChange(r.kode_barang, e.target.value)} 
                          className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[12px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white/50" 
                          placeholder="Beri alasan jika ada selisih..." 
                        />
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-zinc-50/50 border-b border-zinc-200">
                        <td colSpan={7} className="p-0">
                          {renderStockDetailPanel(r)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {defectMoveModal}
      {allocationEditModal}
    </div>
  );
}